"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import type { Vehicle, Location, Extra } from "@/lib/types";
import BookingDateTime from "@/components/BookingDateTime";
import BookingForm from "@/components/BookingForm";
import TierTable from "@/components/TierTable";
import { computeBillableDays } from "@/lib/pricing";

interface VehicleDetailClientProps {
  vehicle: Vehicle;
  locations: Location[];
  extras: Extra[];
  transferEnabled?: boolean;
}

export interface BookingRange {
  date_from: string;
  date_to: string;
  pickup_time: string;
  return_time: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^\d{2}:\d{2}$/;

export default function VehicleDetailClient({ vehicle, locations, extras, transferEnabled }: VehicleDetailClientProps) {
  const searchParams = useSearchParams();
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const pickupTimeParam = searchParams.get("pickup_time");
  const returnTimeParam = searchParams.get("return_time");
  const pickupLocParam = Number(searchParams.get("pickup_location_id"));
  const returnLocParam = Number(searchParams.get("return_location_id"));

  const initialRange: BookingRange | null =
    fromParam && toParam && ISO_DATE.test(fromParam) && ISO_DATE.test(toParam)
      ? {
          date_from: fromParam,
          date_to: toParam,
          pickup_time: pickupTimeParam && HHMM.test(pickupTimeParam) ? pickupTimeParam : "12:00",
          return_time: returnTimeParam && HHMM.test(returnTimeParam) ? returnTimeParam : "12:00",
        }
      : null;

  const initialPickupLocationId = Number.isInteger(pickupLocParam) && pickupLocParam > 0 ? pickupLocParam : null;
  const initialReturnLocationId = Number.isInteger(returnLocParam) && returnLocParam > 0 ? returnLocParam : null;

  const [range, setRange] = useState<BookingRange | null>(initialRange);

  const selectedDays = range
    ? computeBillableDays(range.date_from, range.date_to, range.pickup_time, range.return_time)
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pasirinkite datas</h3>
        <BookingDateTime vehicleId={vehicle.id} initialValue={initialRange} onChange={setRange} />
      </div>

      <TierTable vehicle={vehicle} selectedDays={selectedDays} />

      <BookingForm
        vehicle={vehicle}
        range={range}
        locations={locations}
        extras={extras}
        initialPickupLocationId={initialPickupLocationId}
        initialReturnLocationId={initialReturnLocationId}
        transferEnabled={transferEnabled}
      />
    </div>
  );
}
